import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useContext } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssPageBlock, CssUiComponent } from '@webapp/common/components/survey-custom';
import { getColor, setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import type { GroupMachineActor } from '@webapp/survey/src/machine/group-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import { Comment } from '../../components/comment';

import css from './kano.css';

// То что выводится в опросе и в превью просмотре (при редактировании)
export const KanoRow: FC<{
    questionId: number;
    answer: QuestionAnswerActor;
    answerTexts: Array<string>;
    groupsActors: Array<GroupMachineActor>;
    style: React.CSSProperties;
    labelStyle: React.CSSProperties;
    comment?: boolean;
    commentTextHelp?: string;
    showCantRate?: boolean;
    commentRequired?: boolean;
    cantRateText?: string;
    onNext?: () => void;
}> = ({
    questionId,
    answer: answerActor,
    answerTexts,
    groupsActors,
    style,
    labelStyle,
    comment,
    commentTextHelp,
    showCantRate,
    commentRequired,
    cantRateText,
    onNext,
}) => {
    const answer = useSelector(answerActor, (s) => s.context);
    const {
        response: responseActor,
        valid,
        value: [value1, value2, value3],
    } = answer;

    const response = useSelector(responseActor, (s) => s.context);
    const commentText = response.comment;
    const cantRate = response.value === CANTRATE_ID;
    const { checkbox, fieldBorderColor30, textField: customStyles } = useContext(CustomStylesCtx);
    const isAnswerInvalid = valid === false;
    const isCommentInvalid = isAnswerInvalid && commentRequired && !response.comment;
    const pipingReplacer = usePipingAnswersReplacer();

    const groupOne = groupsActors[0]?.getSnapshot().context;
    const groupTwo = groupsActors[1]?.getSnapshot().context;

    const answerCardVars = {
        '--kano-answer-border': fieldBorderColor30,
        '--kano-answer-accent': checkbox.checked?.backgroundColor,
        '--kano-answer-selected-text': checkbox.checked?.color,
    } as CSSProperties;

    const onChange = useCallback(
        (groupId: number, v: number): void => {
            if (groupId === groupOne?.id) {
                responseActor.send({ type: 'CHANGE', value: v, questionId });
            } else {
                responseActor.send({ type: 'CHANGE_EXTRA', value: v });
            }
            // Auto-advance logic: check if after this change both groups will be filled
            if (comment) {
                return; // Don't auto-advance if comment field is present
            }
            const functional = groupId === groupOne?.id ? v : (response.value as number);
            const dysfunctional = groupId !== groupOne?.id ? v : (response.extra as number);
            if (functional && dysfunctional && onNext) {
                setTimeout(() => {
                    onNext();
                }, 500);
            }
        },
        [responseActor, questionId, groupOne, response.value, response.extra, comment, onNext],
    );

    const onChangeComment = useCallback(
        (v: string): void => {
            responseActor.send({ type: 'CHANGE_COMMENT', value: v });
        },
        [responseActor],
    );

    const handleCantRate = useCallback(
        (checked: boolean) => {
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : undefined, questionId });
            responseActor.send({ type: 'CHANGE_EXTRA', value: undefined });
            // Auto-advance when "Can't rate" is selected
            if (checked && onNext) {
                setTimeout(() => {
                    onNext();
                }, 500);
            }
        },
        [responseActor, questionId, onNext],
    );

    const renderGroupAnswers = (group: { id: number }, currentValue: number | undefined, colIndex: number) => {
        if (!group) return null;

        return (
            <div className={css.colBlock} key={group.id}>
                <div
                    className={cn(CssUiComponent.LABEL, css.label, css.colLabel, {
                        [css.answer_disabled]: cantRate,
                    })}
                    style={style}
                    {...setHtml(pipingReplacer(colIndex === 0 ? value2 : value3))}
                />
                <div
                    className={cn(CssPageBlock.ANSWER, css.answersWrap, { [css.answer_invalid]: isAnswerInvalid })}
                    style={style}
                >
                    {answerTexts.map((answerText, index) => {
                        const checked = !cantRate && currentValue === index + 1;
                        return (
                            <button
                                type='button'
                                key={index}
                                className={cn(css.answerCard, {
                                    [css.answerCardChecked]: checked,
                                    [css.answer_disabled]: cantRate,
                                })}
                                aria-pressed={checked}
                                disabled={cantRate}
                                onClick={() => onChange(group.id, index + 1)}
                            >
                                {answerText}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (answer.hidden || answer.hiddenByAnswersHide) return null;

    return (
        <div className={css.kanoRow} style={answerCardVars}>
            <div
                className={cn(CssUiComponent.LABEL, css.label, css.mainLabel)}
                style={{ ...style, ...labelStyle }}
                {...setHtml(pipingReplacer(value1))}
            />
            <div className={css.colsContainer}>
                {renderGroupAnswers(groupOne, response.value as number, 0)}
                {renderGroupAnswers(groupTwo, response.extra as number, 1)}
            </div>
            {comment && (
                <Comment
                    disabled={cantRate}
                    className={css.commentWrap}
                    commentInvalid={!cantRate && isCommentInvalid}
                    commentTextHelp={commentTextHelp}
                    commentText={commentText}
                    showMark={commentRequired}
                    onChange={onChangeComment}
                    markColor={!cantRate && isCommentInvalid ? getColor('red') : customStyles['--c-border-focus']}
                />
            )}
            {showCantRate && (
                <div className={css.checkboxWrap}>
                    <Checkbox checked={cantRate} label={cantRateText} onChange={handleCantRate} />
                </div>
            )}
        </div>
    );
};
